import React, { useState, useEffect } from 'react';
import { Tag, Plus, Copy, Trash2, ToggleLeft, ToggleRight, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiDelete, apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';
import { AdminConfirmDialog } from '../../components/AdminUi';

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [form, setForm] = useState({
    code: '', discountType: 'percent', discountValue: 10, maxUses: 0,
    validForPlans: '', expiresAt: '', prefix: '', count: 10,
  });

  useEffect(() => { fetchCoupons(); }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const data = await apiGet(API_ROUTES.admin.coupons);
      setCoupons(data.coupons || []);
    } catch (e) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const createSingle = async () => {
    try {
      const res = await apiFetch(API_ROUTES.admin.coupons, {
        method: 'POST',
        body: {
          code: form.code || undefined,
          discountType: form.discountType,
          discountValue: form.discountValue,
          maxUses: form.maxUses,
          validForPlans: form.validForPlans,
          expiresAt: form.expiresAt || undefined,
        },
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      const data = await res.json();
      toast.success(`Coupon created: ${data.code}`);
      setCreating(false);
      fetchCoupons();
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };

  const createBulk = async () => {
    try {
      const res = await apiFetch(`${API_ROUTES.admin.coupons}/bulk`, {
        method: 'POST',
        body: {
          count: form.count,
          prefix: form.prefix,
          discountType: form.discountType,
          discountValue: form.discountValue,
          maxUses: form.maxUses || 1,
          validForPlans: form.validForPlans,
          expiresAt: form.expiresAt || undefined,
        },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      toast.success(`${data.count} coupons generated`);
      setCreating(false);
      fetchCoupons();
    } catch (e) {
      toast.error('Failed to generate coupons');
    }
  };

  const toggleCoupon = async (id, current) => {
    await apiFetch(`${API_ROUTES.admin.coupons}/${id}`, {
      method: 'PATCH',
      body: { isActive: !current },
    });
    fetchCoupons();
  };

  const deleteCoupon = async () => {
    if (!pendingDelete) return;
    await apiDelete(`${API_ROUTES.admin.coupons}/${pendingDelete.id}`);
    toast.success('Deleted');
    setPendingDelete(null);
    fetchCoupons();
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-100">Coupons</h1>
          <p className="text-sm text-gray-500 mt-1">Generate and manage discount codes</p>
        </div>
        <button onClick={() => setCreating(true)} className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all text-sm font-medium">
          <Plus size={16} />
          Create Coupon
        </button>
      </div>

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-card border border-dark-border rounded-xl p-6 max-w-lg w-full">
            <div className="flex items-center gap-3 mb-5">
              <button onClick={() => setBulkMode(false)} className={`px-3 py-1.5 rounded-lg text-sm ${!bulkMode ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500'}`}>Single</button>
              <button onClick={() => setBulkMode(true)} className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${bulkMode ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500'}`}><Layers size={14} /> Bulk</button>
            </div>
            <div className="space-y-4">
              {!bulkMode ? (
                <input type="text" placeholder="Code (leave empty for auto)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="w-full bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200" />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="Prefix (e.g. PROMO)" value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value })}
                    className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200" />
                  <input type="number" placeholder="Count" value={form.count} onChange={(e) => setForm({ ...form, count: parseInt(e.target.value) })}
                    className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <select value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}
                  className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200">
                  <option value="percent">Percentage (%)</option>
                  <option value="fixed">Fixed ($)</option>
                </select>
                <input type="number" placeholder="Value" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: parseFloat(e.target.value) })}
                  className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" placeholder="Max uses (0=unlimited)" value={form.maxUses} onChange={(e) => setForm({ ...form, maxUses: parseInt(e.target.value) })}
                  className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200" />
                <input type="text" placeholder="Valid plans (e.g. pro,elite)" value={form.validForPlans} onChange={(e) => setForm({ ...form, validForPlans: e.target.value })}
                  className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200" />
              </div>
              <input type="date" placeholder="Expiry" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                className="w-full bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200" />
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={bulkMode ? createBulk : createSingle} className="flex-1 bg-purple-500 text-white py-2 rounded-lg hover:bg-purple-600">{bulkMode ? `Generate ${form.count}` : 'Create'}</button>
              <button onClick={() => setCreating(false)} className="flex-1 bg-gray-700 text-white py-2 rounded-lg hover:bg-gray-600">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Coupons table */}
      <div className="glass-card bg-dark-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-dark-border text-gray-500 text-xs uppercase">
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-center px-4 py-3">Discount</th>
              <th className="text-center px-4 py-3">Usage</th>
              <th className="text-center px-4 py-3">Revenue Impact</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-center px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12 text-gray-500">Loading...</td></tr>
            ) : !coupons.length ? (
              <tr><td colSpan={6} className="text-center py-12 text-gray-500">No coupons yet</td></tr>
            ) : (
              coupons.map((cp) => (
                <tr key={cp.id} className="border-b border-dark-border/50 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <code className="text-purple-400 font-mono text-xs bg-purple-500/10 px-2 py-0.5 rounded">{cp.code}</code>
                      <button onClick={() => copyCode(cp.code)} className="text-gray-500 hover:text-gray-300"><Copy size={13} /></button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-300">
                    {cp.discountType === 'percent' ? `${cp.discountValue}%` : `$${cp.discountValue}`}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-400">
                    {cp.usedCount}/{cp.maxUses || '∞'}
                  </td>
                  <td className="px-4 py-3 text-center text-red-400 text-xs">
                    -${cp.totalDiscountGiven?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs ${cp.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
                      {cp.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => toggleCoupon(cp.id, cp.isActive)} className={`p-1 ${cp.isActive ? 'text-green-400' : 'text-gray-600'}`}>
                        {cp.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      </button>
                      <button onClick={() => setPendingDelete(cp)} className="p-1 text-red-400/60 hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
      <AdminConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete coupon?"
        message={pendingDelete ? `${pendingDelete.code} will be permanently removed.` : ''}
        confirmLabel="Delete Coupon"
        requireText={pendingDelete?.code || ''}
        onConfirm={deleteCoupon}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
